import { Card, CardActions, CardContent, Snackbar, SnackbarCloseReason } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports --  allow using normal Button here
import { Button, useAuthState, useNotify, useTranslate } from 'react-admin';

import { GetBackendVersionResponse } from '@Common/types/bo/backend_version';

import { setBackendVersion } from '@sh4res/bo-common/utils/backend_version_store';
import { buildEndpoint, type HttpClient } from '@sh4res/bo-common/utils/build_endpoint';

export const Version: FC<{ poll?: number; httpClient: HttpClient }> = ({
  httpClient,
  poll = 60 * 1000,
}) => {
  const notify = useNotify();
  const translate = useTranslate();
  const [version, setVersion] = useState<GetBackendVersionResponse>();
  const lastFetchedAtRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [isWindowFocused, setIsWindowFocused] = useState(document.hasFocus());
  const { authenticated } = useAuthState();

  const setWindowFocus = useCallback(() => setIsWindowFocused(true), []);
  const setWindowBlur = useCallback(() => setIsWindowFocused(false), []);

  useEffect(() => {
    window.addEventListener('focus', setWindowFocus);
    window.addEventListener('blur', setWindowBlur);

    return () => {
      window.removeEventListener('focus', setWindowFocus);
      window.removeEventListener('blur', setWindowBlur);
    };
  }, [setWindowFocus, setWindowBlur]);

  useEffect(() => {
    let timer: number;

    /* oxlint-disable no-console */
    /* eslint-disable no-console */
    const refreshVersion = async () => {
      if (lastFetchedAtRef.current !== null && lastFetchedAtRef.current + poll >= Date.now()) {
        return;
      }
      lastFetchedAtRef.current = Date.now();

      try {
        const { json: data } = await httpClient(buildEndpoint('backend_version', 'list'));

        if (!version) {
          console.log('Environment:', data.environment);
          console.log('Git SHA:', data.sha);
          console.log('Version:', data.version);
          // used in data provider for logging
          (window as { version_sha?: string }).version_sha = data.sha;
          setVersion(data);
          setBackendVersion(data);
          return;
        }

        if (version.sha !== data.sha || version.version !== data.version) {
          setOpen(true);
        }
      } catch (error: unknown) {
        console.error(error);
      }
    };
    /* oxlint-enable no-console */

    const startPoll = () => {
      // do not await on purpose to avoid race conditions
      refreshVersion();

      if (timer) {
        window.clearInterval(timer);
        timer = 0;
      }

      if (isWindowFocused) {
        timer = window.setInterval(refreshVersion, poll);
      }
    };

    if (authenticated) {
      startPoll();
    }

    return () => {
      if (timer) {
        window.clearInterval(timer);
        timer = 0;
      }
    };
  }, [version, notify, poll, httpClient, isWindowFocused, authenticated]);

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Card>
        <CardContent sx={{ paddingBottom: 1 }}>
          {translate('resources.bo_common.version.new_available')}
        </CardContent>
        <CardActions disableSpacing sx={{ justifyContent: 'center' }}>
          <Button
            disableElevation
            sx={{ margin: '0 !important' }}
            variant="contained"
            size="large"
            onClick={() => location.reload()}
            label={translate('resources.bo_common.version.cta')}
            data-testid="BoExternalVersion_ReloadButton"
          />
        </CardActions>
      </Card>
    </Snackbar>
  );
};
